// stores/session.js - 세션/페어링 상태 관리
import { writable, derived, get } from 'svelte/store';
import { setSentryUser, clearSentryUser } from '../sentry.js';
import { ensurePersistentPairing, getLinkInfo, clearLinkInfo, confirmPendingUploads } from '../pairing.js';
import { uploadQueue } from '../upload-queue.js';

const STORAGE_KEY = 'scantodesk_pairing';
const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.scantodesk.app';

/**
 * @typedef {Object} PairingInfo
 * @property {string} device_id - 세션 ID
 * @property {string} endpoint - API 엔드포인트
 * @property {{ apiKey: string }} credentials - 인증 정보
 * @property {string} paired_at - 페어링 시간
 */

/**
 * @typedef {'connected' | 'connecting' | 'disconnected' | 'checking' | 'reconnecting' | 'error'} ConnectionStatus
 */

/** 세션/페어링 정보 */
export const session = writable(null);

/** 연결 상태 ('checking' = 초기 상태, 아직 확인 중) */
export const connectionStatus = writable('checking');

/** PC 온라인 여부 */
export const pcOnline = writable(false);

/** 연결된 디바이스 이름 */
export const deviceName = writable(null);

/** 구독 상태 */
export const subscription = writable({
  plan: 'free',
  dailyUploadsUsed: 0,
  dailyUploadLimit: 3,
  loaded: false,
});

/** 페어링 여부 (derived) */
export const isPaired = derived(session, $session => $session !== null);

/** 링크 모드 여부 */
export const isLinkMode = writable(false);

/** 링크 정보 */
export const linkInfo = writable(null);

/** 복구 중 여부 (탭 복귀 시 헬스체크 진행 중) */
export const isRecovering = writable(false);

/**
 * 세션 초기화 (localStorage 또는 URL에서)
 * @param {Object} [options]
 * @param {string} [options.shortCode] - 직접 전달된 short code (탭 간 인계 시)
 */
export async function initSession(options = {}) {
  // 0. 직접 전달된 short code (다른 탭에서 인계받은 경우)
  if (options.shortCode) {
    const pairingInfo = await handleShortCodeDirect(options.shortCode);
    if (pairingInfo) {
      session.set(pairingInfo);
      return pairingInfo;
    }
  }

  // 1. Short code URL 확인 (/s/{code})
  const shortCodePairing = await handleShortCodeLink();
  if (shortCodePairing) {
    session.set(shortCodePairing);
    return shortCodePairing;
  }

  // 2. 레거시 딥링크 확인 (?data=)
  const deepLinkPairing = handleDeepLink();
  if (deepLinkPairing) {
    session.set(deepLinkPairing);
    return deepLinkPairing;
  }

  // 3. localStorage에서 복원
  const stored = loadFromStorage();
  if (stored) {
    session.set(stored);
    return stored;
  }

  return null;
}

/**
 * 페어링 여부 확인
 * @returns {boolean}
 */
export function checkPairing() {
  return get(session) !== null;
}

/**
 * 페어링 정보 가져오기 (store 외부용)
 * @returns {PairingInfo | null}
 */
export function getPairingInfo() {
  return get(session);
}

/**
 * 페어링 정보 저장
 * @param {PairingInfo} info
 */
export function savePairingInfo(info) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(info));
    session.set(info);

    // Sentry 사용자 컨텍스트 설정
    setSentryUser(info.device_id, get(deviceName));

    return true;
  } catch (e) {
    console.error('Failed to save pairing info:', e);
    return false;
  }
}

/**
 * 페어링 정보 삭제
 */
export function clearPairingInfo() {
  localStorage.removeItem(STORAGE_KEY);
  session.set(null);
  connectionStatus.set('disconnected');
  pcOnline.set(false);
  clearSentryUser();
}

/**
 * 연결 상태 업데이트
 * @param {ConnectionStatus} status
 * @param {string} [name] - 디바이스 이름
 */
export function updateConnectionStatus(status, name = null) {
  connectionStatus.set(status);
  if (name) {
    deviceName.set(name);
  }
  if (status === 'connected') {
    pcOnline.set(true);
  } else if (status === 'disconnected' || status === 'error') {
    pcOnline.set(false);
  }
}

/**
 * 구독 상태 업데이트
 */
export function updateSubscription(data) {
  subscription.set({
    plan: data.plan || 'free',
    dailyUploadsUsed: data.dailyUploadsUsed || 0,
    dailyUploadLimit: data.dailyUploadLimit || 3,
    loaded: true,
  });
}

/**
 * 낙관적 업로드 카운트 증가 (업로드 성공 시 클라이언트에서 즉시 반영)
 */
export function incrementUploadsUsed() {
  subscription.update(sub => ({
    ...sub,
    dailyUploadsUsed: sub.dailyUploadsUsed + 1,
  }));
}

/**
 * Initialize link session from URL (/l/{code})
 * @returns {Promise<Object|null>} Link info or null
 */
export async function initLinkSession() {
  const path = window.location.pathname;
  const codeMatch = path.match(/^\/l\/([A-HJ-NP-Z2-9]{7})$/i);

  if (!codeMatch) return null;

  const code = codeMatch[1].toUpperCase();
  console.log('Link code detected:', code);

  const response = await fetch(`${API_BASE}/l/${code}`);
  const data = await response.json();

  if (!data.valid) {
    throw new Error(data.error || 'link_invalid');
  }

  const info = {
    code,
    linkId: data.linkId,
    recipientName: data.recipientName,
    desktopOnline: data.desktopOnline,
    expiresAt: data.expiresAt,
  };

  linkInfo.set(info);

  // URL에서 코드 경로 제거
  window.history.replaceState({}, document.title, '/');

  return info;
}

/**
 * Redeem link and create session
 * @param {string} code - Link short code
 * @returns {Promise<Object>} Session info
 */
export async function redeemLink(code) {
  const response = await fetch(`${API_BASE}/l/${code}/redeem`, { method: 'POST' });
  const data = await response.json();

  if (!data.valid) {
    throw new Error(data.error || 'redeem_failed');
  }

  const pairingInfo = {
    device_id: data.sessionId,
    endpoint: data.endpoint,
    credentials: { apiKey: data.apiKey },
    is_link_mode: true,
    paired_at: new Date().toISOString()
  };

  savePairingInfo(pairingInfo);
  isLinkMode.set(true);

  return data;
}

/**
 * Clear link mode state and session
 * Link sessions are ephemeral - clearing returns to clean state
 */
export function clearLinkMode() {
  isLinkMode.set(false);
  linkInfo.set(null);
  clearLinkInfo();
  clearPairingInfo(); // Also clear the ephemeral link session
}

// --- Private helpers ---

function loadFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const info = JSON.parse(stored);
    if (!info.device_id || !info.endpoint || !info.credentials) {
      return null;
    }
    return info;
  } catch (e) {
    console.error('Failed to load pairing info:', e);
    return null;
  }
}

async function handleShortCodeLink() {
  try {
    const path = window.location.pathname;
    const codeMatch = path.match(/^\/s\/([A-HJ-NP-Z2-9]{7})$/i);

    if (!codeMatch) return null;

    const code = codeMatch[1].toUpperCase();
    console.log('Short code detected:', code);

    const response = await fetch(`${API_BASE}/s/${code}`);
    const data = await response.json();

    if (!data.valid) {
      console.error('Short code invalid:', data.error);
      return null;
    }

    // Confirm before overwriting if pending uploads exist
    if (!confirmPendingUploads()) {
      // User cancelled - clean URL and return null (will fall through to existing session)
      window.history.replaceState({}, document.title, '/');
      return null;
    }

    const pairingInfo = {
      device_id: data.sessionId,
      endpoint: data.endpoint,
      credentials: { apiKey: data.apiKey },
      desktop_name: data.deviceName || null,
      desktop_fingerprint: data.fingerprint || null,
      paired_at: new Date().toISOString()
    };

    uploadQueue.clear();
    savePairingInfo(pairingInfo);

    // Create persistent pairing if desktop fingerprint is available
    if (pairingInfo.desktop_fingerprint) {
      ensurePersistentPairing(pairingInfo);
    }

    // Desktop name is available from QR response, but desktop may have gone offline
    // since QR was generated. Set 'connecting' until WebSocket or health check confirms.
    if (data.deviceName) {
      deviceName.set(data.deviceName);
      connectionStatus.set('connecting');
    }

    // URL에서 코드 경로 제거
    window.history.replaceState({}, document.title, '/');

    return pairingInfo;
  } catch (e) {
    console.error('Short code processing failed:', e);
    return null;
  }
}

/**
 * Short code로 직접 세션 생성 (탭 간 인계용, URL 파싱 불필요)
 * @param {string} code - 7자리 short code
 * @returns {Promise<Object|null>}
 */
async function handleShortCodeDirect(code) {
  try {
    console.log('Direct short code handoff:', code);

    const response = await fetch(`${API_BASE}/s/${code}`);
    const data = await response.json();

    if (!data.valid) {
      console.error('Short code invalid:', data.error);
      return null;
    }

    // Confirm before overwriting if pending uploads exist
    if (!confirmPendingUploads()) {
      return null;
    }

    const pairingInfo = {
      device_id: data.sessionId,
      endpoint: data.endpoint,
      credentials: { apiKey: data.apiKey },
      desktop_name: data.deviceName || null,
      desktop_fingerprint: data.fingerprint || null,
      paired_at: new Date().toISOString()
    };

    uploadQueue.clear();
    savePairingInfo(pairingInfo);

    // Create persistent pairing if desktop fingerprint is available
    if (pairingInfo.desktop_fingerprint) {
      ensurePersistentPairing(pairingInfo);
    }

    if (data.deviceName) {
      deviceName.set(data.deviceName);
      connectionStatus.set('connecting');
    }

    return pairingInfo;
  } catch (e) {
    console.error('Direct short code processing failed:', e);
    return null;
  }
}

function handleDeepLink() {
  try {
    const params = new URLSearchParams(window.location.search);
    const data = params.get('data');

    if (!data) return null;

    const urlDecoded = decodeURIComponent(data);
    const decoded = atob(urlDecoded);
    const pairingData = JSON.parse(decoded);

    if (!pairingData.device_id || !pairingData.endpoint || !pairingData.credentials) {
      return null;
    }

    // Confirm before overwriting if pending uploads exist
    if (!confirmPendingUploads()) {
      return null;
    }

    const pairingInfo = {
      device_id: pairingData.device_id,
      endpoint: pairingData.endpoint,
      credentials: pairingData.credentials,
      paired_at: new Date().toISOString()
    };

    uploadQueue.clear();
    savePairingInfo(pairingInfo);

    // URL 파라미터 제거
    const url = new URL(window.location.href);
    url.searchParams.delete('data');
    window.history.replaceState({}, document.title, url.pathname);

    return pairingInfo;
  } catch (e) {
    console.error('Deep link processing failed:', e);
    return null;
  }
}
